import type {ReactElement} from 'react';
import {SkeletonBrick} from '@Components/skeleton-brick/skeleton-brick';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {FOLDER_GRID_COLUMN_COUNT_BELOW_NARROWEST_BAND, skeletonLoaderContainerQuery} from '@Components/design-selector/design-selector.types';
import type {DesignSelectorSkeletonLoaderProps} from './design-selector-skeleton-loader.types';
import styles from './design-selector-skeleton-loader.module.scss';

export function DesignSelectorSkeletonLoader({isVisible, containerRef, folderGridBand, isDesktopVersion}: DesignSelectorSkeletonLoaderProps): ReactElement | null {
  const skeletonParams = useContainerQuery(containerRef, skeletonLoaderContainerQuery);

  if (!isVisible) {
    return null;
  }

  const getFolderLoader = (): ReactElement => {
    const folderLoaderCount = folderGridBand?.columnCount ?? FOLDER_GRID_COLUMN_COUNT_BELOW_NARROWEST_BAND;
    const rightMarginClass = folderGridBand?.gutterClass ?? '';

    const skeletonArray: ReactElement[] = [];
    for (let i = 0; i < folderLoaderCount; i++) {
      const isLastFolder = i === folderLoaderCount - 1;
      skeletonArray.push(<SkeletonBrick isPulsating className={`radius-2 ${isLastFolder ? '' : rightMarginClass}`} height={56} key={`design-selector-skeleton-${i}`} />);
    }
    return <div className="spacing-m-b-3 flex-h-row">{skeletonArray}</div>;
  };

  const getPosterLoader = (): ReactElement => {
    let posterLoaderCount = 0;
    let rightMarginClass = '';
    if (skeletonParams.xxsmall) {
      posterLoaderCount = 1;
    } else if (skeletonParams.xsmall || skeletonParams.small) {
      posterLoaderCount = 3;
      rightMarginClass = 'spacing-m-r-5';
    } else if (skeletonParams.medium) {
      posterLoaderCount = 2;
      rightMarginClass = 'spacing-m-r-6';
    } else if (skeletonParams.large || skeletonParams.xlarge) {
      posterLoaderCount = 3;
      rightMarginClass = 'spacing-m-r-6';
    } else if (skeletonParams.xxlarge || skeletonParams.xxxlarge) {
      posterLoaderCount = 4;
      rightMarginClass = 'spacing-m-r-5';
    }

    const skeletonArray: ReactElement[] = [];
    for (let i = 0; i < posterLoaderCount; i++) {
      const isLastPoster = i === posterLoaderCount - 1;

      skeletonArray.push(
        <div className={isLastPoster ? '' : rightMarginClass} style={{width: '100%'}} key={`design-selector-poster-skeleton-${i}`}>
          <SkeletonBrick isPulsating className="spacing-m-b-4" height={148} />
          <SkeletonBrick isPulsating height={20} width={136} />
        </div>
      );
    }
    return <div className="flex-h-row">{skeletonArray}</div>;
  };

  return (
    <div className={`${styles.loaderWrapperMystuff} ${isDesktopVersion ? styles.desktopChromeSpacing : ''}`}>
      <div className={styles.designSelectorLoaderDesktop}>
        {getFolderLoader()}
        <div className={styles.viewMoreButtonSkeletonContainer}>
          <SkeletonBrick isPulsating className={`radius-4 ${styles.viewMoreButtonSkeleton}`} width={80} height={20} />
        </div>
        {getPosterLoader()}
      </div>
    </div>
  );
}
